import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { workOrdersApi } from '../../api/workOrdersApi';
import { IntakePhoto } from '../../api/intakeApi';
import { ChatPanel } from '../../components/technician/ChatPanel';
import { PhotoGallery } from '../../components/intake/PhotoGallery';
import { performanceApi } from '../../api/performanceApi';
import { useAuthStore } from '../../store/authStore';
import { AlertCircle, ArrowLeft, Clock3, MessageSquare, Pause, Play } from 'lucide-react';
import { useSignalR } from '../../hooks/useSignalR';

interface WorkOrderTask {
  workOrderTaskId: number;
  description: string;
  isCompleted: boolean;
  completedAt?: string;
}

interface WorkOrderDetail {
  workOrderId: number;
  workOrderNumber: string;
  status: string;
  priority: string;
  vehicleYear?: number;
  vehicleMake?: string;
  vehicleModel?: string;
  vehicleColor?: string;
  vin?: string;
  licensePlate?: string;
  mileage?: number;
  customerConcerns?: string;
  description?: string;
  tasks?: WorkOrderTask[];
  sourceIntakeId?: number | null;
  vehicle?: string; vehicleVin?: string; vehiclePlate?: string; mileageIn?: number; customerName?: string;
  vehicleHistory?: Array<{ serviceDate: string; workOrderNumber: string; status: string; description: string; technicianName: string }>;
}

export const JobDetailPage: React.FC = () => {
  const { workOrderId } = useParams<{ workOrderId: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<WorkOrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [showChat, setShowChat] = useState(false);
  const [tasks, setTasks] = useState<WorkOrderTask[]>([]);
  const [photos, setPhotos] = useState<IntakePhoto[]>([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [photosError, setPhotosError] = useState('');
  const staffId = useAuthStore(s => s.staffId);
  const [activeSession, setActiveSession] = useState<{ id: number; workOrderId?: number } | null>(null);
  const [activeBlocker, setActiveBlocker] = useState<{ id: number; workOrderId: number; type: string } | null>(null);
  const [trackingError, setTrackingError] = useState('');
  const { on, off } = useSignalR();

  useEffect(() => {
    if (workOrderId) {
      loadJobDetail(parseInt(workOrderId));
    }
  }, [workOrderId]);

  useEffect(() => {
    if (!workOrderId) return;
    const refresh = (...args: unknown[]) => { if (Number(args[0]) === Number(workOrderId)) void loadJobDetail(Number(workOrderId)); };
    on('WorkOrderStatusChanged', refresh); on('WorkOrderAssigned', refresh);
    return () => { off('WorkOrderStatusChanged', refresh); off('WorkOrderAssigned', refresh); };
  }, [on, off, workOrderId]);

  useEffect(() => {
    if (!staffId) return;
    Promise.all([performanceApi.activeSession(staffId), performanceApi.activeBlocker(staffId)]).then(([session, blocker]) => {
      setActiveSession(session ? { id: session.workSessionId, workOrderId: session.workOrderId } : null);
      setActiveBlocker(blocker ? { id: blocker.workBlockerId, workOrderId: blocker.workOrderId, type: blocker.type } : null);
    }).catch(() => setTrackingError('Time tracking status could not be loaded.'));
  }, [staffId, workOrderId]);

  const toggleWork = async () => {
    if (!staffId || !job) return; setTrackingError('');
    if (activeSession?.workOrderId && activeSession.workOrderId !== job.workOrderId) {
      navigate(`/tech/job/${activeSession.workOrderId}`); return;
    }
    try { if (activeSession) { await performanceApi.stopSession(activeSession.id); setActiveSession(null); }
      else { const x = await performanceApi.startSession(staffId, job.workOrderId, 'Productive'); setActiveSession({ id: x.workSessionId, workOrderId: job.workOrderId }); } }
    catch { setTrackingError('Work timer could not be updated. Stop any timer running on another job first.'); }
  };
  const setBlocker = async (type: string) => {
    if (!staffId || !job) return; setTrackingError('');
    try { if (activeBlocker) { await performanceApi.resolveBlocker(activeBlocker.id); setActiveBlocker(null); }
      else { const x = await performanceApi.startBlocker(staffId, job.workOrderId, type); setActiveBlocker({ id: x.workBlockerId, workOrderId: job.workOrderId, type });
        if (x.stoppedSessionId) setActiveSession(null); } }
    catch { setTrackingError('Blocker status could not be updated. Resolve any blocker on another job first.'); }
  };

  const loadJobDetail = async (id: number) => {
    try {
      setLoading(true);
      const response = await workOrdersApi.getWorkOrderDetail(id);
      setJob(response);
      setTasks(response.tasks || []);
      if (response.sourceIntakeId) {
        setPhotosLoading(true); setPhotosError('');
        workOrdersApi.getPhotos(id).then(setPhotos).catch(() => { setPhotos([]); setPhotosError('Photos could not be loaded.'); }).finally(() => setPhotosLoading(false));
      }
    } catch (error) {
      console.error('Failed to load job:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTaskToggle = async (taskId: number, isCompleted: boolean) => {
    if (!job) return;

    try {
      await workOrdersApi.updateWorkOrderTask(job.workOrderId, taskId, !isCompleted);
      setTasks(tasks.map(t =>
        t.workOrderTaskId === taskId ? { ...t, isCompleted: !isCompleted } : t
      ));
    } catch (error) {
      console.error('Failed to update task:', error);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!job) return;

    try {
      await workOrdersApi.updateWorkOrderStatus(job.workOrderId, newStatus);
      setJob({ ...job, status: newStatus });
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'inprogress':
        return '#7f9bd2';
      case 'scheduled':
      case 'pending':
        return '#bcc7de';
      case 'paused':
        return '#f97316';
      case 'completed':
        return '#10b981';
      default:
        return '#8c7164';
    }
  };

  const completedTasks = tasks.filter(t => t.isCompleted).length;
  const vehicleInfo = job?.vehicle || `${job?.vehicleYear || ''} ${job?.vehicleMake || ''} ${job?.vehicleModel || ''}`.trim() || 'Vehicle details unavailable';

  if (loading) {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <p className="text-secondary">Loading job details...</p>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <p className="text-secondary">Job not found</p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-6 pb-32 md:px-8 md:py-8">
      {/* Header with Back button */}
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={() => navigate('/tech')}
          className="rounded-xl border border-slate-200 bg-white p-2.5 shadow-sm transition-colors hover:bg-slate-50"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div><p className="text-[10px] font-bold uppercase tracking-[.16em] text-slate-400">Work order</p><h1 className="text-sm font-bold text-slate-800">{job.workOrderNumber}</h1></div>
      </div>

      {/* Vehicle Info & Status */}
      <div className="mb-6 rounded-3xl bg-[#0b2039] p-6 text-white shadow-xl shadow-slate-300/40 md:p-8">
        <p className="mb-2 text-xs font-bold uppercase tracking-[.18em] text-orange-400">Assigned vehicle</p><h2 className="mb-5 text-3xl font-bold tracking-tight md:text-4xl">{vehicleInfo}</h2>

        <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <span className="font-code-sm text-code-sm text-secondary bg-surface-container px-3 py-1 rounded">
              RO# {job.workOrderNumber}
            </span>
            <span
              className="font-label-md text-label-md px-3 py-1 rounded-full"
              style={{
                backgroundColor: `${getStatusColor(job.status)}22`,
                color: getStatusColor(job.status),
                border: `1px solid ${getStatusColor(job.status)}40`
              }}
            >
              {job.status}
            </span>
          </div>

          {/* Chat Button */}
          <button
            onClick={() => setShowChat(true)}
            className="flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-orange-600"
          >
            <MessageSquare className="h-5 w-5" />
            Message Front Desk
          </button>
        </div>
      </div>

      {/* Customer Complaint Alert */}
      {job.customerConcerns && (
        <div className="mb-6 rounded-2xl border border-orange-200 bg-orange-50 p-5">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-orange-600" />
            <div>
              <h3 className="font-title-md text-title-md text-on-surface mb-2">Customer Complaint</h3>
              <p className="font-body-md text-body-md text-on-surface italic">"{job.customerConcerns}"</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Vehicle Details Card */}
        <div className="bg-surface-container-lowest border border-border-subtle rounded-xl p-6">
          <h3 className="font-title-md text-title-md text-on-surface mb-4">Vehicle Details</h3>

          <div className="space-y-4">
            {job.customerName && (
              <div>
                <label className="text-label-md text-secondary font-semibold">Vehicle owner</label>
                <p className="font-body-md text-body-md text-on-surface">{job.customerName}</p>
              </div>
            )}

            {(job.vehicleVin || job.vin) && (
              <div>
                <label className="text-label-md text-secondary font-semibold">VIN</label>
                <p className="font-code-sm text-code-sm text-on-surface">{job.vehicleVin || job.vin}</p>
              </div>
            )}

            {(job.vehiclePlate || job.licensePlate) && (
              <div>
                <label className="text-label-md text-secondary font-semibold">License Plate</label>
                <p className="font-body-md text-body-md text-on-surface">{job.vehiclePlate || job.licensePlate}</p>
              </div>
            )}

            {(job.mileageIn || job.mileage) && (
              <div>
                <label className="text-label-md text-secondary font-semibold">Mileage</label>
                <p className="font-body-md text-body-md text-on-surface">{(job.mileageIn || job.mileage)?.toLocaleString()}</p>
              </div>
            )}

            {job.vehicleColor && (
              <div>
                <label className="text-label-md text-secondary font-semibold">Color</label>
                <p className="font-body-md text-body-md text-on-surface">{job.vehicleColor}</p>
              </div>
            )}
          </div>
        </div>

        {/* Service Checklist */}
        <div className="bg-surface-container-lowest border border-border-subtle rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-title-md text-title-md text-on-surface">Service Checklist</h3>
            <span className="text-label-md text-secondary font-semibold">
              {completedTasks}/{tasks.length}
            </span>
          </div>

          {tasks.length === 0 ? (
            <p className="text-body-sm text-secondary">No tasks added yet</p>
          ) : (
            <div className="space-y-3">
              {tasks.map(task => (
                <label
                  key={task.workOrderTaskId}
                  className="flex items-start gap-3 p-3 hover:bg-surface-light rounded-lg transition-colors cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={task.isCompleted}
                    onChange={() => handleTaskToggle(task.workOrderTaskId, task.isCompleted)}
                    className="mt-1 w-5 h-5 rounded"
                    style={{ accentColor: '#f97316' }}
                  />
                  <span
                    className={`font-body-md text-body-md flex-1 ${
                      task.isCompleted ? 'line-through text-secondary' : 'text-on-surface'
                    }`}
                  >
                    {task.description}
                  </span>
                  {task.isCompleted && (
                    <svg className="w-5 h-5 text-primary-container flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                    </svg>
                  )}
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      {job.vehicleHistory && job.vehicleHistory.length > 0 && (
        <div className="mb-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="mb-1 font-title-md text-title-md text-on-surface">Recent vehicle history</h3>
          <p className="mb-4 text-sm text-secondary">Previous workshop visits for this vehicle.</p>
          <div className="divide-y divide-slate-100">
            {job.vehicleHistory.map((visit) => (
              <div key={`${visit.workOrderNumber}-${visit.serviceDate}`} className="grid gap-1 py-4 md:grid-cols-[140px_1fr_auto] md:gap-4">
                <div className="text-sm font-semibold text-slate-700">
                  {new Date(visit.serviceDate).toLocaleDateString()}
                  <div className="text-xs font-normal text-slate-500">{visit.workOrderNumber}</div>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">{visit.description || 'Service visit'}</p>
                  <p className="text-xs text-slate-500">Handled by {visit.technicianName}</p>
                </div>
                <span className="h-fit w-fit rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">{visit.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Intake Photos — read-only, so techs can see what they're working with before starting */}
      {(photos.length > 0 || photosLoading || photosError) && (
        <div className="mb-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="font-title-md text-title-md text-on-surface mb-4">
            Photos ({photos.length}) <span className="text-secondary font-normal">from intake</span>
          </h3>
          {photosLoading ? <p className="text-sm text-slate-500">Loading secure photos…</p> : photosError ? <div className="flex items-center gap-2 text-sm text-red-600"><AlertCircle className="h-4 w-4" />{photosError}<button onClick={() => loadJobDetail(job.workOrderId)} className="font-bold underline">Retry</button></div> : <PhotoGallery photos={photos} />}
        </div>
      )}

      {/* Bottom Action Bar - Desktop and Mobile */}
      <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center gap-2"><Clock3 className="h-5 w-5 text-blue-600" /><h3 className="font-bold text-slate-900">Work tracking</h3></div>
        <div className="flex flex-wrap items-center gap-3"><button onClick={toggleWork} className={`flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-bold ${activeSession?.workOrderId === job.workOrderId ? 'bg-red-600 text-white' : 'bg-[#14345a] text-white'}`}>{activeSession?.workOrderId === job.workOrderId ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}{activeSession?.workOrderId === job.workOrderId ? 'Stop work timer' : activeSession ? 'Open active timed job' : 'Start work timer'}</button>
          <select disabled={!!activeBlocker} onChange={e => e.target.value && setBlocker(e.target.value)} defaultValue="" className="rounded-lg border border-outline-variant bg-white px-4 py-3 text-sm"><option value="">Report a blocker…</option><option value="Parts">Waiting for parts</option><option value="CustomerApproval">Customer approval</option><option value="Equipment">Equipment / bay</option><option value="Dispatch">Dispatch / assignment</option><option value="Warranty">Warranty authorization</option><option value="TechnicalAssistance">Technical assistance</option><option value="Other">Other</option></select>
          {activeBlocker && (activeBlocker.workOrderId === job.workOrderId
            ? <button onClick={() => setBlocker(activeBlocker.type)} className="rounded-lg bg-amber-100 px-4 py-3 text-sm font-semibold text-amber-900">Resolve: {activeBlocker.type}</button>
            : <button onClick={() => navigate(`/tech/job/${activeBlocker.workOrderId}`)} className="rounded-lg bg-amber-100 px-4 py-3 text-sm font-semibold text-amber-900">Open blocked job</button>)}</div>
        {trackingError && <p className="mt-3 text-sm text-status-error">{trackingError}</p>}
        <p className="mt-3 text-xs text-secondary">Tracked time and delays support fair performance analytics. Supervisors verify learner records.</p>
      </div>
      <div className="flex gap-4">
        <button
          onClick={() => handleStatusChange('OnHold')}
          className="flex-1 md:flex-none px-6 py-3 border-2 border-outline-variant text-primary hover:bg-primary-fixed transition-colors font-title-md rounded-lg"
        >
          Pause
        </button>
        <button
          onClick={() => handleStatusChange('Completed')}
          className="flex-1 md:flex-none px-6 py-3 bg-primary-container text-on-primary-container hover:bg-primary transition-colors font-title-md rounded-lg"
        >
          Complete
        </button>
      </div>

      {/* Chat Panel Overlay */}
      {showChat && (
        <ChatPanel
          workOrderId={job.workOrderId}
          vehicleInfo={vehicleInfo}
          onClose={() => setShowChat(false)}
        />
      )}
    </div>
  );
};
